/* NoCodeBackend Aware Secure Vault */
import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { FiLock, FiUnlock, FiKey, FiEye, FiEyeOff, FiCopy, FiRefreshCw, FiTrash2, FiPause, FiPlay, FiShield, FiAlertTriangle, FiPlus, FiSearch, FiClock, FiActivity, FiX, FiInfo, FiCheck, FiSettings, FiFileText } = FiIcons;
import { mockVaultSecrets, mockVaultAudit } from '../data/mockData';

const showToast = (message, type = 'success') => {
  window.dispatchEvent(new CustomEvent('show-toast', { detail: { message, type } }));
};

const UnlockModal = ({ onUnlock, onClose }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (password === 'demo123') { 
      onUnlock(); 
    } else { 
      setError('Incorrect password. Access denied.'); 
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md z-[100] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border border-slate-200 animate-in zoom-in-95 duration-300">
        <div className="p-8 space-y-6 text-center">
          <div className="w-20 h-20 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto shadow-inner border border-blue-100">
            <SafeIcon icon={FiLock} className="text-4xl" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-slate-800">Vault Access Control</h3>
            <p className="text-sm text-slate-500 mt-2">Enter administrative password to decrypt secrets.</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <input autoFocus type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••••••" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:border-blue-500 text-center font-mono" />
            {error && <p className="text-xs font-bold text-red-500">{error}</p>}
            <p className="text-[10px] text-slate-400 italic">Demo Password: demo123</p>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 py-3 text-sm font-bold text-slate-400 hover:text-slate-600 transition-colors">Cancel</button>
              <button type="submit" className="flex-1 py-3 bg-slate-900 text-white rounded-xl text-sm font-bold shadow-lg hover:bg-slate-800 transition-all">Unlock Vault</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const SecureVault = () => {
  const [isLocked, setIsLocked] = useState(true);
  const [secrets, setSecrets] = useState(mockVaultSecrets);
  const [auditLogs, setAuditLogs] = useState(mockVaultAudit);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeModal, setActiveModal] = useState(null);
  const [revealedIds, setRevealedIds] = useState([]);
  const [autoLockTimer, setAutoLockTimer] = useState(300);

  useEffect(() => {
    let interval;
    if (!isLocked && autoLockTimer > 0) {
      interval = setInterval(() => setAutoLockTimer(prev => prev - 1), 1000);
    } else if (autoLockTimer === 0 && !isLocked) {
      handleLock();
    }
    return () => clearInterval(interval);
  }, [isLocked, autoLockTimer]);

  const addAudit = (action, target = '') => {
    const newLog = { id: `v_aud_${Date.now()}`, time: 'Just now', user: 'Admin (AD)', action, target, status: 'Success' };
    setAuditLogs([newLog, ...auditLogs]);
  };

  const handleUnlock = () => { 
    setIsLocked(false); 
    setActiveModal(null); 
    setAutoLockTimer(300); 
    addAudit('Vault Unlocked'); 
  };

  const handleLock = () => { 
    setIsLocked(true); 
    setRevealedIds([]); 
    addAudit('Vault Locked'); 
  };

  const toggleReveal = (id, name) => {
    if (revealedIds.includes(id)) { 
      setRevealedIds(revealedIds.filter(rid => rid !== id)); 
    } else { 
      setRevealedIds([...revealedIds, id]); 
      addAudit('Secret Viewed', name); 
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const filteredSecrets = secrets.filter(s => s.name.toLowerCase().includes(searchTerm.toLowerCase()) || s.type.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="space-y-6 animate-in slide-in-from-left-4 duration-500 pb-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <SafeIcon icon={FiShield} className="text-blue-600" /> Secure Vault
          </h2>
          <p className="text-sm text-slate-500">Encrypted storage for provider and system-level credentials.</p>
        </div>
        <div className="flex items-center gap-3">
          {!isLocked && (
            <div className={`px-4 py-2 rounded-xl border flex items-center gap-2 text-xs font-bold ${autoLockTimer < 30 ? 'bg-red-50 text-red-600 animate-pulse' : 'bg-blue-50 text-blue-600'}`}>
              <SafeIcon icon={FiClock} /> Auto-Lock: {formatTime(autoLockTimer)}
            </div>
          )}
          <button onClick={() => isLocked ? setActiveModal('unlock') : handleLock()} className={`px-5 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 shadow-md ${isLocked ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-slate-900 text-white hover:bg-slate-800'}`}>
            <SafeIcon icon={isLocked ? FiUnlock : FiLock} /> {isLocked ? 'Unlock Vault' : 'Lock Vault'}
          </button>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-4">
        <SafeIcon icon={FiAlertTriangle} className="text-amber-600 text-xl mt-0.5 shrink-0" />
        <p className="text-xs text-amber-800 leading-relaxed font-medium">
          <b>Administrative Note:</b> Master keys are stored in the <code>secure_vault_secrets</code> NoCodeBackend collection. The frontend only receives masked values; actual decryption is performed by a secure Netlify Function upon authorized PIN verification.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 space-y-4">
          <div className="flex bg-white p-2 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex-1 flex items-center px-3 gap-2">
              <SafeIcon icon={FiSearch} className="text-slate-400" />
              <input type="text" placeholder="Search vault secrets..." className="w-full text-sm outline-none bg-transparent" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {isLocked ? (
              <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center space-y-6">
                <SafeIcon icon={FiLock} className="text-4xl text-slate-300 mx-auto" />
                <h4 className="text-lg font-bold text-slate-800">Vault is Locked</h4>
                <button onClick={() => setActiveModal('unlock')} className="px-8 py-3 bg-blue-600 text-white rounded-xl text-sm font-bold shadow-lg">Enter Password</button>
              </div>
            ) : (
              filteredSecrets.map(secret => (
                <div key={secret.id} className="bg-white border border-slate-200 rounded-2xl shadow-sm hover:border-blue-200 transition-all p-5 flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl ${secret.env === 'live' ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>
                      <SafeIcon icon={FiKey} />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-800">{secret.name}</h3>
                      <p className="text-[10px] text-slate-400 font-bold uppercase">{secret.type}</p>
                    </div>
                  </div>
                  <div className="flex-1 bg-slate-50 rounded-xl p-3 border border-slate-100 flex items-center justify-between gap-4">
                    <div className="flex-1 font-mono text-[11px] text-slate-600 truncate">
                      {revealedIds.includes(secret.id) ? secret.value : '••••••••••••••••••••••••••••'}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => toggleReveal(secret.id, secret.name)} className="p-1.5 text-slate-400 hover:text-blue-600 transition-all">
                        <SafeIcon icon={revealedIds.includes(secret.id) ? FiEyeOff : FiEye} />
                      </button>
                      <button onClick={() => { navigator.clipboard.writeText(secret.value); addAudit('Secret Copied', secret.name); showToast('Secret copied.'); }} className="p-1.5 text-slate-400 hover:text-blue-600 transition-all">
                        <SafeIcon icon={FiCopy} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              <SafeIcon icon={FiFileText} className="text-slate-400" /> Vault Audit
            </h3>
          </div>
          <div className="p-4 space-y-4 max-h-[400px] overflow-y-auto custom-scrollbar">
            {auditLogs.map(log => (
              <div key={log.id} className="relative pl-5 pb-4 border-l border-slate-100 last:pb-0">
                <div className="absolute left-[-5px] top-1 w-2.5 h-2.5 rounded-full bg-slate-200 border-2 border-white"></div>
                <div className="space-y-1">
                  <p className="text-[11px] font-bold text-slate-800">{log.action}</p>
                  <p className="text-[9px] text-slate-400">{log.time} • {log.user}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {activeModal === 'unlock' && <UnlockModal onClose={() => setActiveModal(null)} onUnlock={handleUnlock} />}
    </div>
  );
};

export default SecureVault;